"use client";

// @i18n-only en — Christmas cards are sold in the US only.

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { CARD_COUNTDOWN_FROM, CARD_ORDER_DEADLINE, cardsOpen } from "@/lib/cards";

type Props = {
  /** "Order by Nov 30 for Christmas delivery" */
  label: string;
  /** The count from Nov 25, with {days} in it: "5 days left to order for Christmas delivery". */
  countdownDays: string;
  /** The count's last 24 hours. */
  lastDay: string;
  /** What the bar says once the deadline has passed. */
  closed: string;
};

/**
 * "Order by Nov 30 for Christmas delivery", from Nov 25 the days left to Nov 30 23:59 ET, and after
 * it the line that says ordering has closed. Days, not a ticking clock: hours and minutes read as a
 * fake-scarcity timer to older buyers. The lander is a server page outside the funnel, so its copy
 * arrives as props rather than through a client translation namespace. The server renders the plain
 * line (the page is edge-cached); the count and the closed line only start in the browser. It sticks
 * to the top of the screen, so the date stays in sight while they scroll and fill in.
 */
export function CardsDeadlineBar({ label, countdownDays, lastDay, closed }: Props) {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const deadline = CARD_ORDER_DEADLINE.getTime();
  let text = label;
  if (now !== null && !cardsOpen(now)) {
    text = closed;
  } else if (now !== null && now >= CARD_COUNTDOWN_FROM.getTime() && now < deadline) {
    const daysLeft = Math.ceil((deadline - now) / 86_400_000);
    text = daysLeft <= 1 ? lastDay : countdownDays.replace("{days}", String(daysLeft));
  }

  return (
    <div className="sticky top-0 z-30 bg-ink px-4 py-2 text-center font-sans text-sm font-medium text-paper" data-testid="cards-deadline">
      <span aria-hidden="true" className="mr-2 text-gold">✦</span>
      {text}
    </div>
  );
}

/** The same bar on the card builder steps and the card editor, which read the funnel's messages. */
export function CardsFunnelBar() {
  const t = useTranslations("cards.deadline");
  return <CardsDeadlineBar closed={t("closed")} countdownDays={t.raw("countdownDays") as string} label={t("topBar")} lastDay={t("lastDay")} />;
}
